import { Tooltip } from '@mui/material'
import {
  useMfrrCmBids,
  useMfrrConfig,
  useMfrrDeliveredBids,
  useMfrrPublishedBids,
  usePublishMfrrCmBidsMutation,
  useSubmitMfrrCmBidsMutation,
  useUnpublishMfrrCmBidsMutation,
  useUpdateMfrrCmBidsMutation,
} from 'api/mfrr/mfrr.api'
import type { EnabledMfrrProductionUnit, MfrrBid } from 'api/mfrr/mfrr.types'
import { type ReactNode, useMemo, useReducer, useState, useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'
import { colors } from 'styles/variables'
import Card from 'ui/atoms/Card/Card'
import NewButton from 'ui/atoms/NewButton/NewButton'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import ConfirmDialog from 'ui/components/ConfirmDialog/ConfirmDialog'
import InfoBanner from 'ui/components/InfoBanner/InfoBanner'
import TooltipIconButton from 'ui/components/TooltipIconButton/TooltipIconButton'
import UiConfig from 'ui/uiConfig/UiConfig'
import Datetime from 'utils/datetime/datetime'
import { capitalize } from 'utils/stringUtils'
import {
  getCurrentMinuteMarker,
  hasCMMarketClosedForDeliveryDate,
  isMfrrDateTooFarInFuture,
  MINUTE_IN_MS,
  parseTimeString,
  subscribeToMinuteBoundary,
} from '../MFRRTradingDeadlineCounter/MFRRTradingDeadlineCounter.helper'
import { MfrrBidTable } from '../MfrrBidTable/MfrrBidTable'
import { CM_ROW_COUNT, EMPTY_MFRR_BIDS, mapMfrrBidsToRowValues } from '../MfrrBidTable/MfrrBidTable.helper'
import type { MfrrBidTableEligibility } from '../MfrrBidTable/MfrrBidTable.types'
import { MfrrLastDeliveredStatus } from '../MfrrLastDeliveredStatus/MfrrLastDeliveredStatus'
import {
  getCanEmptyMfrrTable,
  getEffectiveMfrrSavedBids,
  getLatestMfrrBidsPerSlot,
  getMfrrBidActionAvailability,
  getMfrrBidStatusInfo,
  getMfrrDeliveryWindowEnd,
  mapMfrrBidIdsForUpdate,
  normalizeMfrrBidsForComparison,
} from './MFRRCMTrading.helper'
import styles from './MFRRCMTrading.module.less'

const CM_MARKET = 'cm' as const

type DialogState = {
  unlockConfirmOpen: boolean
  emptyTableConfirmOpen: boolean
  clearEditableRowsSignal: number | undefined
}

type DialogAction =
  | { type: 'openUnlockConfirm' }
  | { type: 'closeUnlockConfirm' }
  | { type: 'openEmptyTableConfirm' }
  | { type: 'closeEmptyTableConfirm' }
  | { type: 'incrementClearSignal' }

function dialogReducer(state: DialogState, action: DialogAction): DialogState {
  switch (action.type) {
    case 'openUnlockConfirm':
      return { ...state, unlockConfirmOpen: true }
    case 'closeUnlockConfirm':
      return { ...state, unlockConfirmOpen: false }
    case 'openEmptyTableConfirm':
      return { ...state, emptyTableConfirmOpen: true }
    case 'closeEmptyTableConfirm':
      return { ...state, emptyTableConfirmOpen: false }
    case 'incrementClearSignal':
      return {
        ...state,
        emptyTableConfirmOpen: false,
        clearEditableRowsSignal: (state.clearEditableRowsSignal ?? 0) + 1,
      }
    default:
      return state
  }
}

const INITIAL_DIALOG_STATE: DialogState = {
  unlockConfirmOpen: false,
  emptyTableConfirmOpen: false,
  clearEditableRowsSignal: undefined,
}

type MFRRCMTradingProps = {
  units: EnabledMfrrProductionUnit[]
  systemId: number | undefined
  dateStartTime: ISODateTime
  hasConfigError: boolean
  isConfigLoading: boolean
}

export function MFRRCMTrading({
  units,
  systemId,
  dateStartTime,
  hasConfigError,
  isConfigLoading,
}: MFRRCMTradingProps): ReactNode {
  const { t } = useTranslation()
  const { hasAccess } = useAuth()

  const { data: mfrrConfig } = useMfrrConfig(systemId)
  const cmGateClosureTime = mfrrConfig?.cm_gate_closure_time
  // Memoized: a fresh object here on every render feeds MfrrBidTable's disabledHours
  // useMemo, which cascades into a setState-in-effect loop that never stabilizes.
  const cmGateClosureTimeComponents = useMemo(
    () => (cmGateClosureTime ? parseTimeString(cmGateClosureTime) : undefined),
    [cmGateClosureTime]
  )

  const dateEndTime = getMfrrDeliveryWindowEnd(dateStartTime)

  const { data: savedBids = EMPTY_MFRR_BIDS, isPending: isBidsLoading } = useMfrrCmBids({
    systemId,
    market: CM_MARKET,
    startTime: dateStartTime,
    endTime: dateEndTime,
  })

  const { data: publishedBids = EMPTY_MFRR_BIDS } = useMfrrPublishedBids({
    systemId,
    market: CM_MARKET,
    startTime: dateStartTime,
    endTime: dateEndTime,
  })

  // Bids for the same slot can carry multiple rows across statuses (e.g. a stale delivered/
  // published bid alongside a newer draft); keep only the latest per slot so old values don't win.
  const latestSavedBids = useMemo(() => getLatestMfrrBidsPerSlot(savedBids), [savedBids])
  const isLocked = latestSavedBids.length > 0 && latestSavedBids.every((bid) => bid.status === 'published')
  const hasDraftBids = latestSavedBids.length > 0 && !isLocked
  const hasCMDecisionSupportGraph = hasAccess({
    submodule: 'mfrr_cm_decision_support_graph',
    module: 'mfrrtrading',
  })

  const { data: deliveredBids = EMPTY_MFRR_BIDS } = useMfrrDeliveredBids({
    systemId,
    market: CM_MARKET,
    startTime: dateStartTime,
    endTime: dateEndTime,
  })
  const hasDraftBidsForDeliveredDate = hasDraftBids && deliveredBids.length > 0

  const initialValues = useMemo(
    () => mapMfrrBidsToRowValues(latestSavedBids, dateStartTime, units, CM_ROW_COUNT, CM_MARKET),
    [latestSavedBids, dateStartTime, units]
  )

  const submitMutation = useSubmitMfrrCmBidsMutation()
  const updateMutation = useUpdateMfrrCmBidsMutation()
  const publishMutation = usePublishMfrrCmBidsMutation()
  const unpublishMutation = useUnpublishMfrrCmBidsMutation()

  const [currentPayload, setCurrentPayload] = useState<MfrrBid[]>([])
  const [eligibility, setEligibility] = useState<MfrrBidTableEligibility>({ canSubmit: true, disabledReason: '' })
  const [dialogState, dispatchDialogAction] = useReducer(dialogReducer, INITIAL_DIALOG_STATE)
  const minuteMarker = useSyncExternalStore(subscribeToMinuteBoundary, getCurrentMinuteMarker, getCurrentMinuteMarker)
  const now = minuteMarker * MINUTE_IN_MS

  const savedBidsSignature = useMemo(
    () =>
      normalizeMfrrBidsForComparison(
        getEffectiveMfrrSavedBids(latestSavedBids, publishedBids, (startTime) =>
          hasCMMarketClosedForDeliveryDate(
            startTime,
            now,
            cmGateClosureTimeComponents,
            mfrrConfig?.cm_input_margin_closure_time,
            mfrrConfig?.cm_lead_days
          )
        )
      ),
    [
      latestSavedBids,
      publishedBids,
      now,
      cmGateClosureTimeComponents,
      mfrrConfig?.cm_input_margin_closure_time,
      mfrrConfig?.cm_lead_days,
    ]
  )
  const currentBidsSignature = useMemo(() => normalizeMfrrBidsForComparison(currentPayload), [currentPayload])
  const hasLocalChanges = currentBidsSignature !== savedBidsSignature

  const isSaving = submitMutation.isPending || updateMutation.isPending
  const { canSave, canLock } = getMfrrBidActionAvailability({
    isBidsLoading,
    isLocked,
    hasDraftBids,
    eligibilityCanSubmit: eligibility.canSubmit,
    hasSystemId: systemId !== undefined,
    payloadCount: currentPayload.length,
    hasLocalChanges,
    isSaving,
    isPublishing: publishMutation.isPending,
  })
  const isDateTooFarInFuture = isMfrrDateTooFarInFuture(dateStartTime, now, mfrrConfig?.cm_max_bid_days_ahead)
  const isCmDeadlinePassed = hasCMMarketClosedForDeliveryDate(
    dateStartTime,
    now,
    cmGateClosureTimeComponents,
    mfrrConfig?.cm_input_margin_closure_time,
    mfrrConfig?.cm_lead_days
  )
  const canSaveForSelectedDate = canSave && !isDateTooFarInFuture && !isCmDeadlinePassed
  const canLockForSelectedDate = canLock && !isDateTooFarInFuture && !isCmDeadlinePassed
  const hasEditableValues = currentPayload.some((bid) => bid.volume !== 0 || bid.price !== 0)
  const canEmptyTable =
    getCanEmptyMfrrTable({
      isBidsLoading,
      isLocked,
      hasUnits: units.length > 0,
      hasEditableValues,
    }) &&
    !isCmDeadlinePassed &&
    !isDateTooFarInFuture
  const canShowActionButtons = !isCmDeadlinePassed && !isDateTooFarInFuture

  const handleSaveClick = () => {
    if (systemId === undefined) {
      return
    }

    const payload = { system_id: systemId, bids: currentPayload }

    if (!hasDraftBids) {
      submitMutation.mutate({ payload })
      return
    }

    const payloadWithIds = {
      system_id: systemId,
      bids: mapMfrrBidIdsForUpdate(currentPayload, latestSavedBids),
    }

    updateMutation.mutate({ payload: payloadWithIds })
  }

  const handleLockClick = () => {
    if (systemId === undefined) {
      return
    }

    publishMutation.mutate({
      payload: {
        system_id: systemId,
        market: CM_MARKET,
        start_time: dateStartTime,
        end_time: dateEndTime,
      },
    })
  }

  const handleUnlockClick = () => {
    dispatchDialogAction({ type: 'openUnlockConfirm' })
  }

  const handleEmptyTableClick = () => {
    dispatchDialogAction({ type: 'openEmptyTableConfirm' })
  }

  const handleUnlockConfirm = () => {
    dispatchDialogAction({ type: 'closeUnlockConfirm' })

    if (systemId === undefined) {
      return
    }

    unpublishMutation.mutate({
      payload: {
        system_id: systemId,
        market: CM_MARKET,
        start_time: dateStartTime,
        end_time: dateEndTime,
      },
    })
  }

  const handleEmptyTableConfirm = () => {
    dispatchDialogAction({ type: 'incrementClearSignal' })
  }

  const bidStatusInfo = getMfrrBidStatusInfo(latestSavedBids, isLocked)
  const localDate = Datetime.toLocalTime(dateStartTime, 'onlyDate')
  const todayDate = Datetime.toLocalTime(Datetime.getTodayDate().startTime, 'onlyDate')
  const tomorrowDate = Datetime.toLocalTime(Datetime.getTomorrowDate().startTime, 'onlyDate')
  const titleDate =
    localDate === todayDate
      ? `${t('Today')} ${localDate}`
      : localDate === tomorrowDate
        ? `${t('Tomorrow')} ${localDate}`
        : `${capitalize(Datetime.toLocalTime(dateStartTime, 'shortDayText'))} ${localDate}`

  return (
    <>
      {hasCMDecisionSupportGraph && (
        <Card title={t('CM Price Decision Support')} collapsible rememberCollapsed>
          <UiConfig
            type="mfrr_cm_decision_support_graph"
            datasetStartTime={dateStartTime}
            datasetEndTime={dateEndTime}
          />
        </Card>
      )}
      <Card title={t('Place bids for CM ({{date}})', { date: titleDate })}>
        {canShowActionButtons && (
          <div className={styles.MFRRCMTrading_TitleAction}>
            {!isLocked && canEmptyTable && (
              <NewButton onClick={handleEmptyTableClick} label={t('Clear table')} variant="cancel" />
            )}
            {!isLocked && (
              <Tooltip title={!canSaveForSelectedDate ? t('No changes to save') : ''} arrow>
                <span>
                  <NewButton
                    onClick={handleSaveClick}
                    label={t('Save bid')}
                    variant="edit"
                    disabled={!canSaveForSelectedDate}
                    loading={isSaving}
                  />
                </span>
              </Tooltip>
            )}
            {isLocked ? (
              <div className={styles.MFRRCMTrading_UnlockButton}>
                <TooltipIconButton
                  onClick={handleUnlockClick}
                  icon="fal fa-lock"
                  tooltip={t('Unlock bid')}
                  iconColor={colors.primary5}
                  disabled={isDateTooFarInFuture}
                />
              </div>
            ) : (
              <Tooltip title={!canLockForSelectedDate ? t('No values to be locked') : ''} arrow>
                <span>
                  <NewButton
                    onClick={handleLockClick}
                    label={t('Lock bid')}
                    variant="edit"
                    disabled={!canLockForSelectedDate}
                    loading={publishMutation.isPending}
                    style={
                      canLockForSelectedDate
                        ? {
                            backgroundColor: colors.warning4,
                            color: colors.black,
                          }
                        : undefined
                    }
                  />
                </span>
              </Tooltip>
            )}
          </div>
        )}
        {bidStatusInfo && (
          <p className={styles.MFRRCMTrading_StatusText}>
            {t(bidStatusInfo.isLocked ? 'Locked by {{name}} at {{time}}' : 'Last saved by {{name}} at {{time}}', {
              name: bidStatusInfo.name,
              time: bidStatusInfo.time,
            })}
          </p>
        )}
        <MfrrLastDeliveredStatus
          systemId={systemId}
          market={CM_MARKET}
          units={units}
          dateStartTime={dateStartTime}
          dateEndTime={dateEndTime}
          isDeadlinePassed={isCmDeadlinePassed}
        />
        {hasDraftBidsForDeliveredDate && !isCmDeadlinePassed && (
          <div className="w-fit">
            <InfoBanner
              style="warning"
              text={t(
                'This date has delivered bids. Your unpublished changes will not be in effect until you publish them again.'
              )}
            />
          </div>
        )}
        <div className={`${styles.MFRRCMTrading_Row} ${styles.MFRRCMTrading_Content}`}>
          {isConfigLoading ? <p>{t('Loading mFRR-CM configuration...')}</p> : null}
          {hasConfigError ? <InfoBanner style="danger" text={t('Failed to load mFRR-CM configuration.')} /> : null}
          {!isConfigLoading && !hasConfigError && units.length === 0 ? (
            <InfoBanner style="warning" text={t('No production units are configured for mFRR-CM.')} />
          ) : null}
          {!isConfigLoading && !hasConfigError && units.length > 0 ? (
            <div className={styles.MFRRCMTrading_Row}>
              <MfrrBidTable
                market={CM_MARKET}
                units={units}
                dateStartTime={dateStartTime}
                initialValues={initialValues}
                publishedBids={publishedBids}
                clearEditableRowsSignal={dialogState.clearEditableRowsSignal}
                isReadOnly={isBidsLoading || isLocked || isDateTooFarInFuture || isCmDeadlinePassed}
                cmGateClosureTimeComponents={cmGateClosureTimeComponents}
                cmInputMarginClosureTime={mfrrConfig?.cm_input_margin_closure_time}
                cmLeadDays={mfrrConfig?.cm_lead_days}
                onEligibilityChange={setEligibility}
                onPayloadChange={setCurrentPayload}
              />
            </div>
          ) : null}
        </div>
      </Card>

      <ConfirmDialog
        open={dialogState.emptyTableConfirmOpen}
        onClose={() => dispatchDialogAction({ type: 'closeEmptyTableConfirm' })}
        onConfirm={handleEmptyTableConfirm}
        textObject={{
          title: t('Clear table'),
          text: t('Are you sure you want to clear all bids?'),
          confirm: t('Clear'),
          deny: t('Cancel'),
        }}
      />

      <ConfirmDialog
        open={dialogState.unlockConfirmOpen}
        onClose={() => dispatchDialogAction({ type: 'closeUnlockConfirm' })}
        onConfirm={handleUnlockConfirm}
        textObject={{
          title: t('Unlock bid'),
          text: t('Are you sure you want to edit/unlock these bids?'),
          confirm: t('Unlock'),
          deny: t('Cancel'),
        }}
      />
    </>
  )
}
